<template>
  <Popup :ref="(el) => popup = el"
         :hide-title="true"
         @onClose="hide"
  >
    <form class="popup__form" @submit.prevent="submit">
      <div class="popup__content">
        <div class="ban-user__header mb-sm">
          <span class="ban-user__label">{{ helper.trans('labels.ban_user') }}</span>
        </div>
        <div v-if="userId" class="ban-user__row mb-sm">
          <span class="ban-user__label">{{ helper.trans('labels.user_id') }}:</span>
          <span class="ban-user__value">{{ userId }}</span>
        </div>
        <div class="ban-user__option mb-sm">
          <input type="checkbox"
                 class="form-check-input"
                 id="banPermanent"
                 v-model="permanent">
          <label class="form-check-label" for="banPermanent">
            {{ helper.trans('labels.permanent_ban') }}
          </label>
        </div>
        <div v-if="!permanent" class="ban-user__field mb-sm">
          <label class="ban-user__label" for="banDuration">{{ helper.trans('labels.duration_seconds') }}</label>
          <input type="number"
                 id="banDuration"
                 class="form-control"
                 min="1"
                 v-model.number="durationSeconds"
                 :placeholder="helper.trans('labels.duration_seconds')">
        </div>
        <div v-if="validationError" class="alert alert-danger mb-sm" role="alert">
          {{ validationError }}
        </div>
      </div>
      <div class="popup__controls">
        <button type="button" @click.prevent="hide" class="btn popup__close">
          <span class="text-capitalize-first">{{ $t('button.cancel') }}</span>
        </button>
        <LoaderButton :on-click="submit" class="btn btn--accept">
          <span class="text-capitalize-first">{{ $t('button.ban') }}</span>
        </LoaderButton>
      </div>
    </form>
  </Popup>
</template>

<script lang="ts">
import { defineComponent, ref, watch } from 'vue';
import { $t, helper } from '@/service/CommonHelper';
import { api } from '@/service/APIClient';
import Popup from '@/apps/public/components/Popup.vue';
import LoaderButton from '@/apps/public/components/LoaderButton.vue';
import { useNotification } from '@kyvg/vue3-notification';

const notification = useNotification();

export default defineComponent({
  methods: { $t },
  components: {
    Popup,
    LoaderButton,
  },
  emits: ['banned'],
  setup(_, { emit }) {
    const popup = ref<InstanceType<typeof Popup> | null>(null);
    const requisitesId = ref<number | null>(null);
    const userId = ref<string | null>(null);
    const storeHash = ref<string | null>(null);
    const permanent = ref(true);
    const durationSeconds = ref<number | ''>(86400);
    const validationError = ref('');

    const show = (reqId: number, uid: string, hash: string) => {
      requisitesId.value = reqId;
      userId.value = uid;
      storeHash.value = hash;
      permanent.value = true;
      durationSeconds.value = 86400;
      validationError.value = '';
      popup.value?.show();
    };

    const hide = () => {
      popup.value?.hide();
      requisitesId.value = null;
      userId.value = null;
      storeHash.value = null;
      validationError.value = '';
    };

    watch(permanent, (v) => {
      if (v) {
        validationError.value = '';
      }
    });

    const submit = async () => {
      if (requisitesId.value == null || userId.value == null || !storeHash.value) return;

      if (!permanent.value) {
        const sec = durationSeconds.value;
        if (sec === '' || sec == null || sec < 1) {
          validationError.value = helper.trans('labels.duration_seconds_required');
          return;
        }
      }
      validationError.value = '';

      try {
        await api.requisitesCounterpartyBan(requisitesId.value, userId.value, {
          storeHash: storeHash.value,
          permanent: permanent.value,
          ...(permanent.value ? {} : { durationSeconds: Number(durationSeconds.value) }),
        });
        notification.notify({ title: helper.trans('labels.ban_success') || 'User banned', type: 'success' });
        hide();
        emit('banned');
      } catch (e: any) {
        const msg = e?.response?.data?.message || e?.message || helper.trans('labels.error');
        validationError.value = msg;
        notification.notify({ title: msg, type: 'error' });
      }
    };

    return {
      popup,
      userId,
      permanent,
      durationSeconds,
      validationError,
      helper,
      show,
      hide,
      submit,
    };
  },
});
</script>

<style scoped>
.popup__form {
  display: flex;
  flex-direction: column;
  min-width: 320px;
}
.popup__content {
  padding: 0 0 1.5rem 0;
}
.popup__controls {
  margin-top: 0.5rem;
  padding-top: 1rem;
  border-top: 1px solid var(--border-color, #e9ecef);
}
.ban-user__header {
  font-weight: 600;
  font-size: 1rem;
}
.ban-user__row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
}
.ban-user__label {
  color: var(--text-secondary, #6c757d);
  flex-shrink: 0;
}
.ban-user__value {
  font-family: ui-monospace, monospace;
  word-break: break-all;
}
.ban-user__option {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.ban-user__option .form-check-input {
  margin: 0;
}
.ban-user__field .ban-user__label {
  display: block;
  margin-bottom: 0.25rem;
}
.ban-user__field .form-control {
  width: 100%;
  max-width: 200px;
}
.mb-sm {
  margin-bottom: 0.75rem;
}
.mb-sm:last-child {
  margin-bottom: 0;
}
</style>
